import { useQuery } from "@tanstack/react-query";
import { useApi } from "@ncp/api-client";
import { formatAed, formatAedNear, formatIndexNear, formatNumber, formatNumberNear, formatPercentNear, useLocale, type Locale, type Marks } from "@ncp/i18n";

/** One measure of the published calculation method (GET /api/analysis/method). */
export interface MethodMeasure {
  key: string;
  nameEn: string;
  nameAr: string;
  definitionEn: string;
  definitionAr: string;
  formula: string;
  inputsEn: string[];
  inputsAr: string[];
  thresholds: Array<{ key: string; labelEn: string; labelAr: string; value: number; unit: string }>;
}

export interface MethodBands {
  hhiModerate: number;
  hhiHigh: number;
  deltaHhiSignificant: number;
  deltaHhiMinor: number;
  deltaHhiPresumption: number;
  concentrationRatioN: number;
  marketShareThresholdPercent: number;
  turnoverThresholdAed: number;
  riskLevelHighFrom: number;
  riskLevelMediumFrom: number;
  guppiWatchPercent: number;
  guppiConcernPercent: number;
  defaultGrossMarginPercent: number;
  shareVerificationTolerancePoints: number;
  financialStatementYears: number;
  /** Condition 2.16: the bands every confidence percentage is read against (high from, medium from), set in the analysis policy. */
  confidenceHighFromPercent: number;
  confidenceMediumFromPercent: number;
  /** Condition 2.9: the other marks a figure is printed beside – the control stakes, the simplified procedure's shares, the ex-post tolerance. */
  controlMajorityPercent?: number;
  controlDeFactoFromPercent?: number;
  dealControlAcquiredFromPercent?: number;
  dealControlPossibleFromPercent?: number;
  simplifiedCombinedSharePercent?: number;
  simplifiedCombinedShareWithSmallDeltaPercent?: number;
  simplifiedVerticalSharePercent?: number;
  exPostHhiTolerance?: number;
  dealValueBandsAed?: number[];
}

export interface CalculationMethod {
  version: string;
  status: "Proposed" | "Approved";
  statusEn: string;
  statusAr: string;
  aggregatedLinesEn: string;
  aggregatedLinesAr: string;
  measures: MethodMeasure[];
  bands: MethodBands;
  /** Condition 2.16: the confidence each basis of a rule's reading carries, in percent – the only values a stage-review reading can show. */
  readingConfidences?: Array<{ key: string; percent: number; meaningEn: string; meaningAr: string }> | null;
}

export type MeasureKey =
  | "hhi" | "deltaHhi" | "crN" | "combinedShare" | "unallocatedShare" | "riskScore" | "guppi" | "cmcr" | "criticalLoss" | "assumedMargin"
  | "turnoverGroup" | "turnoverState" | "turnoverMarket" | "shareStatus" | "completeness" | "efficiencyCredit"
  // Scores outside the merger file, published with the method (condition 2.16).
  | "dealScreeningScore" | "marketScreeningScore" | "marketSignalScore" | "draftQuality"
  // Where each figure comes from and when it may be quoted (condition 2.3), and the two confidence percentages (condition 2.16).
  | "figureCheck" | "criterionConfidence" | "draftConfidence"
  // The risk score of every file outside merger control (condition 2.1).
  | "fileRiskScore"
  // The scores the file risk of complaints, infringement reports and exemptions starts from (condition 2.16).
  | "triagePriority" | "article9Score"
  // The readiness score of an application before it is filed, the fine calculation of the enforcement view, the legal assistant's confidence,
  // the Data Bank's figure confidence, the merger simulation and local catchments, and the operational statistics (condition 2.16).
  | "filingReadiness" | "penaltyIndication" | "assistantConfidence" | "dataBankConfidence" | "mergerSimulation" | "localCatchment" | "marketStudyAnalyses" | "operationalStatistics";

/** The method changes only when a release publishes a new version of it: one fetch per session is enough. */
export function useCalculationMethod(enabled = true) {
  const api = useApi();
  return useQuery({ queryKey: ["analysis", "method"], queryFn: () => api.get<CalculationMethod>("/api/analysis/method"), staleTime: 10 * 60_000, enabled });
}

/**
 * The thresholds of the published method as a sentence names them (condition 2.14): `{{turnover}}`, `{{share}}`, and the GUPPI marks of a
 * locale string. Dashes until the method has loaded – a text never states a value of its own.
 */
export function thresholdWords(bands: MethodBands | undefined, locale: Locale): { turnover: string; share: string; guppiWatch: string; guppiConcern: string } {
  if (!bands) return { turnover: "—", share: "—", guppiWatch: "—", guppiConcern: "—" };
  return { turnover: formatAed(bands.turnoverThresholdAed, locale), share: `${bands.marketShareThresholdPercent}%`, guppiWatch: `${bands.guppiWatchPercent}%`, guppiConcern: `${bands.guppiConcernPercent}%` };
}

/** High | Medium | Low – the band a confidence (0–1 or a percentage) reads in on the published method's bands; null until the method has loaded. */
export function confidenceBandOf(value: number, bands: Pick<MethodBands, "confidenceHighFromPercent" | "confidenceMediumFromPercent"> | undefined): "High" | "Medium" | "Low" | null {
  if (!bands) return null;
  const pct = value <= 1 ? value * 100 : value;
  return pct >= bands.confidenceHighFromPercent ? "High" : pct >= bands.confidenceMediumFromPercent ? "Medium" : "Low";
}

/** A threshold of a published measure (the readiness and completeness bands); null until the method has loaded. */
export function measureThreshold(method: CalculationMethod | undefined, measure: MeasureKey, key: string): number | null {
  return method?.measures.find((m) => m.key === measure)?.thresholds.find((t) => t.key === key)?.value ?? null;
}

/**
 * Calculation method 1.0 took effect with release 1.3. An agent draft made before it quotes figures the method has since replaced: the
 * analysis page shows the current figures and folds such a draft away as superseded (7.2: one value per measure on a page).
 */
export const METHOD_SINCE = "2026-10-19T00:00:00Z";

export interface ShareLine { name: string; sharePercent: number; kind: "Party" | "Competitor" | "Unallocated" }

export interface MarketFigures {
  productMarket: string;
  productMarketAr?: string | null;
  geographicMarket: string;
  combinedShare: number;
  hhiPre: number;
  hhiPost: number;
  deltaHhi: number;
  cr4Pre: number;
  cr4Post: number;
  concentrationRatioN: number;
  band: string;
  assessment: string;
  assessmentAr?: string | null;
  overlap: boolean;
  mostAffected: boolean;
  unallocatedShare: number;
  unallocatedLines: string[];
  sharesTotal: number;
  sharesDoNotAddUp: boolean;
  hhiPostIfUnallocatedOneFirm: number;
  identifiedFirms: number;
  overlapType: string;
  marketSizeAed: number;
  partiesSalesInMarketAed?: number | null;
  lines: ShareLine[];
  methodVersion: string;
}

export interface EconomicsFigures {
  party1: string; party2: string; share1Percent: number; share2Percent: number;
  guppi1Percent: number; guppi2Percent: number; cmcr1Percent: number; cmcr2Percent: number;
  marginPercent: number; marginAssumed: boolean; guppiBand: string;
  criticalLossPercent: number; actualLossPercent: number; candidateMarketPasses: boolean; candidateMarketDetermined: boolean; breakEvenRecapturePercent: number; ssnipPercent: number;
  readingEn: string; readingAr: string;
}

export interface RiskFactor { factor: string; points: number; rationale: string; factorAr: string; rationaleAr: string }

export interface CaseFigures {
  methodVersion: string;
  methodStatus: "Proposed" | "Approved";
  anyOverlap: boolean;
  mostAffectedMarket?: string | null;
  /** theoriesOfHarm: the theories the market's current figures support (server rule, finding 5.2), listed while no current draft exists. */
  /** traced: the market's size, shares and indices traced to their sources (condition 2.3). */
  markets: Array<{ marketId: string; figures: MarketFigures; economics?: EconomicsFigures | null; theoriesOfHarm?: Array<{ en: string; ar: string }> | null; traced?: TracedFigure[] | null }>;
  risk: { score?: number | null; level?: "High" | "Medium" | "Low" | null; factors: RiskFactor[]; basisMarket?: string | null; basisMarketAr?: string | null; horizontal: boolean; verticalLink: boolean; sensitiveSector: boolean; storedScore?: number | null };
  turnover: TracedFigure[];
}

export type TracedFigureKey = "group" | "state" | "market" | "marketSize" | "share" | "combinedShare" | "hhiPre" | "hhiPost" | "deltaHhi" | "crPre" | "crPost"
  // The figures of the files outside the concentrations (condition 2.3): an amendment's stated shares and amounts, a complaint's estimated harm, a fine range's sales basis.
  | "amendedShare" | "amendedAmount" | "estimatedHarm" | "salesBasis";

/**
 * One figure of the file with its source, the period it covers and the date its source was recorded (condition 2.3); a figure read from a
 * document names the document, page and passage. A calculated or extracted figure is "unchecked" until an officer checks it against its
 * source, and a figure calculated from an unchecked one stays unchecked while it is; until then no approved report, decision or letter quotes it.
 */
export interface TracedFigure {
  key: TracedFigureKey; value?: number | null; unit: "AED" | "%" | "points"; valueAed?: number | null;
  status: "Declared" | "Entered" | "Calculated" | "Extracted" | "NotRecorded"; sourceEn: string; sourceAr: string; nameEn: string; nameAr: string;
  productMarket?: string | null; productMarketAr?: string | null; party?: string | null; partyKind?: "Party" | "Competitor" | "Unallocated" | null;
  period?: string | null; periodAr?: string | null; sourceDate?: string | null; marketId?: string | null;
  documentId?: string | null; documentName?: string | null; page?: number | null; excerpt?: string | null;
  inputs: string[]; uncheckedInputsEn: string[]; uncheckedInputsAr: string[];
  subjectKey: string; unchecked: boolean; checkedBy?: string | null; checkedAt?: string | null; checkNote?: string | null;
  /** On a file outside the concentrations: the form field ("combinedMarketShare", "estimatedHarmAed", "salesBasisAed") or the amendment change ("change:2") it stands in. */
  field?: string | null;
}

export function useCaseFigures(caseId: string) {
  const api = useApi();
  return useQuery({ queryKey: ["analysis", "figures", caseId], queryFn: () => api.get<CaseFigures>(`/api/analysis/cases/${caseId}/figures`) });
}

/**
 * Every figure of a file traced to its source, whatever its procedure (condition 2.3): an exemption's combined share, the shares and amounts an
 * amendment states, a complaint's estimated harm, the sales basis of a fine range (and a merger file's turnover and market figures).
 */
export function useTracedFigures(caseId: string) {
  const api = useApi();
  return useQuery({ queryKey: ["analysis", "figures", caseId, "traced"], queryFn: () => api.get<{ figures: TracedFigure[] }>(`/api/analysis/cases/${caseId}/figures/traced`) });
}

/** High | Medium | Low on the one risk scale, read from the published method. */
export function riskLevelOf(score: number, bands: Pick<MethodBands, "riskLevelHighFrom" | "riskLevelMediumFrom">): "High" | "Medium" | "Low" {
  return score >= bands.riskLevelHighFrom ? "High" : score >= bands.riskLevelMediumFrom ? "Medium" : "Low";
}

/** Condition 2.9: the platform's one rule for a figure read against a threshold or a target (shown in full where rounding would hide its side). */
export { digitsNear } from "@ncp/i18n";

export { markSets } from "./marks";
import { markSets } from "./marks";

/** A figure printed in full until the method has loaded, so a screen never rounds a figure against marks it does not yet know. */
const inFull = (v: number, locale: Locale) => formatNumber(v, locale, { maximumFractionDigits: 4 });

/**
 * The marks of the published method with the platform's one rule for a figure printed beside them (condition 2.9): a turnover or a sales
 * figure against the AED 300 million mark, a share against the 40% mark, an HHI against its bands, a change in HHI against the ΔHHI marks, a
 * GUPPI against the watch and concern marks, and a stake against the control marks, each in full wherever rounding would hide which side of
 * the mark it stands on (Data Bank, Deal Watch, enforcement, the merger file). Until the method has loaded, the figure is printed in full.
 */
export function useThresholdFigures() {
  const method = useCalculationMethod();
  const { locale } = useLocale();
  const b = method.data?.bands;
  const m = markSets(b);
  const pct = (v: number | null | undefined, marks: Marks) => (v == null ? "—" : b ? formatPercentNear(v, locale, marks) : `${inFull(v, locale)}%`);
  const index = (v: number | null | undefined, marks: Marks) => (v == null ? "—" : b ? formatIndexNear(v, locale, marks) : inFull(v, locale));
  return {
    aed: (v: number | null | undefined, fractionDigits = 1) => (v == null ? "—" : b ? formatAedNear(v, locale, b.turnoverThresholdAed, fractionDigits) : formatAed(v, locale)),
    share: (v: number | null | undefined) => pct(v, m.share),
    hhi: (v: number | null | undefined) => index(v, m.hhi),
    delta: (v: number | null | undefined) => index(v, m.delta),
    guppi: (v: number | null | undefined) => pct(v, m.guppi),
    stake: (v: number | null | undefined) => pct(v, m.control),
    dealStake: (v: number | null | undefined) => pct(v, m.dealControl),
    /** A percentage beside marks of the caller's own reading (a tolerance, a target). */
    percent: pct,
    /** A number (points, weeks) beside marks of the caller's own reading. */
    number: (v: number | null | undefined, marks: Marks, digits = 1) => (v == null ? "—" : formatNumberNear(v, locale, marks, digits)),
    marks: m,
    turnoverMark: b?.turnoverThresholdAed,
    shareMark: b?.marketShareThresholdPercent,
  };
}
